import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useBlocker } from "react-router";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { FormStepper } from "sj-ui-toolkit/src/components";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { isSingleNonEnterpriseBundle } from "sj-ui-toolkit/src/constants/bundles";

import {
  createTournamentAsync,
  updateTournamentAsync,
  buildTournamentFormData,
} from "@/api/tournaments";
import { useTournamentData } from "@/hooks/useTournamentData";
import { useTournamentValidation } from "@/hooks/useTournamentValidation";
import { useTournamentFilterData } from "@/hooks/useTournamentFilterData";
import { useTournamentLessons } from "@/hooks/useTournamentLessons";
import { useAvailableUsers } from "@/hooks/useAvailableUsers";
import { useUnsavedUsersWarning } from "@/hooks/useUnsavedUsersWarning";
import { buildFilterOptions } from "@/helpers/tournamentFilters";
import type { FilterGroup } from "@/helpers/tournamentFilters";
import UserSelectionConfirmDialog from "@/components/assignments/UserSelectionConfirmDialog";

import { TournamentDescription } from "@/components/tournaments/TournamentDescription";
import { TournamentAudience } from "@/components/tournaments/TournamentAudience";
import { TournamentContent } from "@/components/tournaments/TournamentContent";
import { TournamentSettings } from "@/components/tournaments/TournamentSettings";
import { TournamentReview } from "@/components/tournaments/TournamentReview";

import type { TournamentFormState } from "@/api/tournaments";
import type { LessonFilters } from "@/hooks/useTournamentLessons";

// "Coding Exercises" is legacy tournament-only content with no bundle data of its own (see
// tournamentFilters.ts), so restricted tenants have it stripped out here instead.
const excludeExperimentOnly = (groups: FilterGroup[]): FilterGroup[] =>
  groups.map(group =>
    group.label !== "activityType"
      ? group
      : { ...group, filters: group.filters.filter(f => f.value !== "experiment_only") }
  );

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TournamentForm() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const isEditMode = Boolean(tournamentId);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const tenantName = getCurrentSelectedTenant() ?? "";
  const { contentBundles } = useUserContext();
  // Same "limited catalog" check the learner Catalog uses to restrict content by bundle.
  const isLimitedContentBundle =
    contentBundles === null || isSingleNonEnterpriseBundle(contentBundles);

  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccessful, setSubmissionSuccessful] = useState(false);
  const cancelConfirmedRef = useRef(false);
  const [lessonFilters, setLessonFilters] = useState<LessonFilters>({
    language: [],
    technology: [],
    role: [],
    level: [],
    activityType: [],
    release: [],
  });

  // ─── Data hooks ─────────────────────────────────────────────────────────

  const { tournament, setTournament, isInitialized, isOngoing } = useTournamentData(
    tournamentId,
    isEditMode
  );
  const { validationErrors, isFormValid, showValidationMessages, validateSection } =
    useTournamentValidation(currentStep);
  const { filterData } = useTournamentFilterData(tenantName);
  const { tournamentLessons, isLoading: isLoadingLessons } = useTournamentLessons(
    lessonFilters,
    !isLimitedContentBundle
  );
  const { availableUsers, allUsers } = useAvailableUsers(tenantName);

  const {
    showUserSelectionDialog,
    selectedUsersToAdd,
    setSelectedUsersToAdd,
    selectedUsersToRemove,
    setSelectedUsersToRemove,
    addUsersAndContinueRef,
    handleSelectedUsersChange,
    handleContinue,
    handleCancel,
    handleAddUsersAndContinue,
    checkUnsavedUsers,
  } = useUnsavedUsersWarning(setCurrentStep);

  // ─── Breadcrumb ─────────────────────────────────────────────────────────

  useBreadcrumb(
    isEditMode
      ? {
          label: tournament.basicInfo.name || "Edit Tournament",
          route: AdminAppRoutes.routes.tournaments.routes.id.route.replace(
            ":tournamentId",
            tournamentId ?? ""
          ),
          display: "page",
        }
      : {
          label: "Create Tournament",
          route: AdminAppRoutes.routes.tournaments.routes.new.route,
          display: "page",
        }
  );

  // ─── Validate on edit mode load ─────────────────────────────────────────

  useEffect(() => {
    if (isEditMode && isInitialized) {
      validateSection("basicInfo", tournament.basicInfo);
      validateSection("audience", tournament.audience);
      validateSection("content", tournament.content);
      validateSection("settings", tournament.settings);
    }
    // Only run when isInitialized changes from false to true
  }, [isInitialized]);

  // ─── Form handlers ───────────────────────────────────────────────────────

  const allFilterOptions = buildFilterOptions(filterData);
  const filterOptions = isLimitedContentBundle
    ? excludeExperimentOnly(allFilterOptions)
    : allFilterOptions;

  const handleFormChange = useCallback(
    (
      section: keyof TournamentFormState,
      newValues: TournamentFormState[keyof TournamentFormState]
    ) => {
      setTournament(prev => ({ ...prev, [section]: newValues }));
      validateSection(section, newValues);
    },
    [validateSection, setTournament]
  );

  const handleStepChange = (newStep: number) => {
    if (checkUnsavedUsers(currentStep, newStep, 1)) return;
    setCurrentStep(newStep);
  };

  // ─── useBlocker ─────────────────────────────────────────────────────────

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !submissionSuccessful &&
      !cancelConfirmedRef.current &&
      currentStep > 0 &&
      currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      confirmDialog({
        message: "Changes you made will not be saved.",
        header: "Are you sure?",
        defaultFocus: "reject",
        acceptClassName: "p-button-danger",
        icon: "pi pi-exclamation-circle",
        acceptLabel: "Confirm",
        rejectLabel: "Cancel",
        headerStyle: { border: "none" },
        accept: () => blocker.proceed(),
        reject: () => blocker.reset(),
      });
    }
  }, [blocker]);

  // ─── Close guard ──────────────────────────────────────────────────────────

  const handleClose = () => {
    confirmDialog({
      message: "Changes you made will not be saved.",
      header: "Are you sure?",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      icon: "pi pi-exclamation-circle",
      acceptLabel: "Confirm",
      rejectLabel: "Cancel",
      headerStyle: { border: "none" },
      accept: () => {
        cancelConfirmedRef.current = true;
        navigate(AdminAppRoutes.routes.tournaments.route);
      },
    });
  };

  // ─── Submit ──────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const payload = buildTournamentFormData(tournament);
      if (isEditMode && tournamentId) {
        await updateTournamentAsync(tournamentId, payload);
      } else {
        await createTournamentAsync(payload);
      }
      setSubmissionSuccessful(true);
      cancelConfirmedRef.current = true;
      showToast({
        severity: "success",
        detail: isEditMode ? "Tournament updated successfully" : "Tournament created successfully",
      });
      navigate(AdminAppRoutes.routes.tournaments.route);
    } catch (error) {
      console.error("Error saving tournament:", error);
      showToast({
        severity: "error",
        detail: isEditMode ? "Failed to update tournament" : "Failed to create tournament",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Steps ───────────────────────────────────────────────────────────────

  const steps = [
    {
      header: showValidationMessages
        ? `Name & Description${!validationErrors.nameDescription.hasError ? " ✓" : " ✗"}`
        : "Name & Description",
      title: "Name & Description",
      content: (
        <TournamentDescription
          value={tournament.basicInfo}
          onChange={basicInfo => handleFormChange("basicInfo", basicInfo)}
          showValidationMessages={showValidationMessages}
          isTournamentInProgress={isOngoing}
        />
      ),
    },
    {
      header: showValidationMessages
        ? `Audience${!validationErrors.audience.hasError ? " ✓" : " ✗"}`
        : "Audience",
      title: "Audience",
      content: (
        <TournamentAudience
          availableUsers={availableUsers}
          allUsers={allUsers}
          audience={tournament.audience}
          onChangeAudience={audience => handleFormChange("audience", audience)}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.audience}
          isEditMode={isEditMode}
          selectedUsersToAdd={selectedUsersToAdd}
          setSelectedUsersToAdd={setSelectedUsersToAdd}
          selectedUsersToRemove={selectedUsersToRemove}
          setSelectedUsersToRemove={setSelectedUsersToRemove}
          onSelectedUsersChange={handleSelectedUsersChange}
          onAddUsersAndContinue={addUsersAndContinueRef}
        />
      ),
    },
    {
      header: showValidationMessages
        ? `Content${!validationErrors.content.hasError ? " ✓" : " ✗"}`
        : "Content",
      title: "Content",
      content: (
        <TournamentContent
          availableLessons={tournamentLessons}
          selectedLessons={tournament.content.selected}
          filters={lessonFilters}
          filterOptions={filterOptions}
          onChangeFilters={setLessonFilters}
          onChangeLessons={selected =>
            handleFormChange("content", { ...tournament.content, selected })
          }
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.content}
          isTournamentInProgress={isOngoing}
          isLoadingLessons={isLoadingLessons}
        />
      ),
    },
    {
      header: showValidationMessages
        ? `Tournament Settings${
            !(
              validationErrors.settings.startDate.hasError ||
              validationErrors.settings.endDate.hasError
            )
              ? " ✓"
              : " ✗"
          }`
        : "Tournament Settings",
      title: "Tournament Settings",
      content: (
        <TournamentSettings
          settings={tournament.settings}
          onChangeSettings={settings => handleFormChange("settings", settings)}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.settings}
          isTournamentInProgress={isOngoing}
        />
      ),
    },
    {
      header: "Summary",
      title: "Summary",
      content: (
        <TournamentReview
          tournament={tournament}
          onStepChange={setCurrentStep}
          validationErrors={validationErrors}
        />
      ),
    },
  ];

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h1>{isEditMode ? "Edit Tournament" : "Create New Tournament"}</h1>
        <Button
          icon="pi pi-times"
          onClick={handleClose}
          severity="contrast"
          aria-label="Close and go back to tournaments"
          data-testid="tournament-form-close-button"
        />
      </div>
      <Card data-testid="tournament-form-stepper">
        <FormStepper
          steps={steps}
          currentStep={currentStep}
          setCurrentStep={handleStepChange}
          handleSubmit={() => void handleSubmit()}
          isNew={!isEditMode}
          isSubmitting={isSubmitting}
          saveButtonText="Save Tournament"
          submitDisabled={!isFormValid || isSubmitting}
        />
      </Card>

      <UserSelectionConfirmDialog
        visible={showUserSelectionDialog}
        onHide={handleCancel}
        onContinue={handleContinue}
        onAddUsersAndContinue={handleAddUsersAndContinue}
        selectedUsersCount={selectedUsersToAdd.length}
      />

      {/* Loading overlay while fetching existing tournament data */}
      <Dialog
        visible={!isInitialized}
        modal
        closable={false}
        showHeader={false}
        style={{ width: "300px", height: "300px" }}
        maskStyle={{ backdropFilter: "blur(5px)" }}
        onHide={() => undefined}
      >
        <div className="flex flex-col items-center justify-center gap-4 h-full">
          <p>Loading Tournament Data</p>
          <ProgressSpinner aria-label="Loading" style={{ width: "50px", height: "50px" }} />
        </div>
      </Dialog>

      <ConfirmDialog />
    </>
  );
}
